/**
 * The chunked transcript's outline: the legacy pipeline fed by a synthetic
 * event stream reconstructed from the skeleton.
 *
 * The legacy viewer's outline is NOT a function of the raw event tree — it
 * renders the timeline-selected "main" view: `buildTimeline` synthesizes a
 * root agent named "main" (solvers content unwrapped, init prepended,
 * scorers renamed "scoring"), the default swimlane selection collects that
 * view's events (`collectRawEvents`, utility spans excluded), re-treeifies
 * them (`buildEventNodes`), and only then applies the outline visitors and
 * turn/scoring grouping (`TranscriptOutline.outlineNodeList`).
 *
 * `legacyOutlineNodes` composes those REAL exported stages — nothing here is
 * a port, so it cannot drift. The chunked path runs the same composition
 * over `syntheticEventsFromSkeleton` (see its fidelity contract); the twin
 * test (`mainViewOutline.test.ts`) pins synthetic-fed output against
 * real-events-fed output across the fixture corpus.
 */
import type { Event } from "@tsmono/inspect-common/types";
import {
  buildEventNodes,
  buildOutlineNodeList,
  buildTimeline,
  collectRawEvents,
  computeFlatSwimlaneRows,
  correctRetryTimestamps,
  filterEmptyBranches,
  getSelectedSpans,
  kSandboxSignalName,
  parseSelection,
  spanHasBranches,
  type EventNode,
  type TimelineSpan,
} from "@tsmono/inspect-components/transcript";

import {
  syntheticEventsFromSkeleton,
  type SampleSkeleton,
} from "../../../../log_data";

export interface LegacyOutline {
  /** Selected-view name shown as the outline header (root row = "main"). */
  header: string;
  /** Flat outline rows (post visitors, turn and scoring grouping). */
  nodes: EventNode[];
}

export interface OutlineViewTree {
  eventNodes: EventNode[];
  defaultCollapsedIds: Record<string, true>;
}

/**
 * The EventNode tree for a selected timeline view's collected events — the
 * exact `TranscriptLayout.eventsForNodes` + `useEventNodes` composition.
 * This is the shape `TranscriptOutline` consumes directly.
 */
export const outlineViewTree = (
  selectedEvents: Event[],
  sourceSpans: ReadonlyMap<string, TimelineSpan>,
  hiddenTypes: readonly string[]
): OutlineViewTree => {
  const eventsForNodes = selectedEvents.filter(
    (e) => !hiddenTypes.includes(e.event)
  );
  const { eventNodes, defaultCollapsedIds } = buildEventNodes(
    eventsForNodes,
    false,
    sourceSpans
  );
  return { eventNodes, defaultCollapsedIds };
};

/**
 * Flat outline rows for a selected view — `outlineViewTree` plus the real
 * `buildOutlineNodeList` visitor/turn/scoring chain (headless twin of what
 * the component renders; used by tests).
 */
export const outlineNodesForView = (
  selectedEvents: Event[],
  sourceSpans: ReadonlyMap<string, TimelineSpan>,
  hiddenTypes: readonly string[]
): EventNode[] => {
  const { eventNodes, defaultCollapsedIds } = outlineViewTree(
    selectedEvents,
    sourceSpans,
    hiddenTypes
  );
  return buildOutlineNodeList(eventNodes, defaultCollapsedIds);
};

/**
 * The exact outline row derivation the legacy viewer performs for an
 * at-rest sample with the default swimlane selection. Headless twin of the
 * `useTranscriptTimeline` + `outlineNodesForView` composition the chunked
 * panel runs (the panel's selection is interactive; this pins the default).
 */
export const legacyOutlineNodes = (
  rawEvents: Event[],
  hiddenTypes: readonly string[]
): LegacyOutline => {
  // TranscriptLayout.eventsForTimeline: anchors are exempt from the filter
  const eventsForTimeline = rawEvents.filter(
    (e) => e.event === "anchor" || !hiddenTypes.includes(e.event)
  );
  // useTranscriptTimeline / useTimelinesArray
  const corrected = correctRetryTimestamps(eventsForTimeline);
  const timeline = filterEmptyBranches(buildTimeline(corrected));
  // useTimelineConfig: showBranches' unset default = branches present
  const showBranches = spanHasBranches(timeline.root);
  const rows = computeFlatSwimlaneRows(timeline.root, {
    includeUtility: false,
    showBranches,
  });
  // useTimeline: default selection = root row
  const selected = rows[0]?.key ?? null;
  const spans = getSelectedSpans(rows, selected);
  const collected =
    spans.length === 0
      ? { events: corrected, sourceSpans: new Map<string, TimelineSpan>() }
      : collectRawEvents(spans, {
          includeUtility: false,
          regionIndex: parseSelection(selected)?.regionIndex ?? null,
          showBranches,
        });
  return {
    header: timeline.root.name,
    nodes: outlineNodesForView(
      collected.events,
      collected.sourceSpans,
      hiddenTypes
    ),
  };
};

export interface ChunkedOutlineRow {
  id: string;
  depth: number;
  label: string;
  /** Ordinal in the real event sequence to scroll to, when known. */
  anchor?: number;
}

export interface ChunkedOutline {
  header: string;
  rows: ChunkedOutlineRow[];
}

/** Row label, mirroring the legacy `OutlineRow.labelForNode` cases that can
 * occur for synthetic nodes (agent/branch card nodes carry sourceSpans the
 * synthetic stream never produces). */
const rowLabel = (node: EventNode): string => {
  const event = node.event;
  if (event.event === "span_begin" || event.event === "step") {
    return event.name === kSandboxSignalName ? "sandbox events" : event.name;
  }
  switch (event.event) {
    case "subtask":
      return event.name;
    case "model":
      return "model";
    case "score":
      return "scoring";
    case "checkpoint":
      return `checkpoint ${event.checkpoint_id}`;
    default:
      return event.event;
  }
};

export const outlineRowsFromNodes = (
  nodes: EventNode[],
  ordinals: ReadonlyMap<string, number>
): ChunkedOutlineRow[] =>
  nodes.map((node) => ({
    id: node.id,
    depth: node.depth,
    label: rowLabel(node),
    anchor: ordinals.get(node.id),
  }));

export const chunkedOutline = (
  skeleton: SampleSkeleton,
  hiddenTypes: readonly string[]
): ChunkedOutline => {
  const { events, ordinals } = syntheticEventsFromSkeleton(skeleton);
  const { header, nodes } = legacyOutlineNodes(events, hiddenTypes);
  return {
    header,
    rows: outlineRowsFromNodes(nodes, ordinals),
  };
};
